import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import InputField from '../../components/ui/InputField';
import ArcadeGamePoster from './ArcadeGamePoster';

const DEFAULT_GAME_ICON = '/images/icons/Logo.svg';

type DisplayMode = 'card5' | 'card10' | 'list';

const GAMES_PER_PAGE: Record<DisplayMode, number> = {
  card5: 5,
  card10: 10,
  list: 10,
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  height: '48px',
  padding: '12px 16px',
  border: '1.2px solid #2E2E3C',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'Pretendard',
  backgroundColor: '#1C1C28',
  color: '#F4F4F8',
  cursor: 'pointer',
};

interface Game {
  id: string;
  title: string;
  description: string;
  gameUrl: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  schoolName: string;
  rating: number;
  playCount: number;
  createdAt: Date;
  iconUrl?: string;
  grade?: number;
  class?: number;
}

const Store: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  // reviews 컬렉션에서 직접 집계한 게임별 평균 별점 — 비로그인 게스트가 매긴 별점까지 즉시 반영
  const [ratingsMap, setRatingsMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('card5');
  const [selectedSchool, setSelectedSchool] = useState<string>('');
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [page, setPage] = useState(1);
  const gamesPerPage = GAMES_PER_PAGE[displayMode];
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const { isMobile } = useLayoutContext();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const gamesQuery = query(
          collection(db, 'games'),
          orderBy(sortBy, 'desc')
        );
        
        const querySnapshot = await getDocs(gamesQuery);
        const gamesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        })) as Game[];

        setGames(gamesData);
      } catch (error) {
        console.error('Error fetching games:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [sortBy]);

  // reviews 컬렉션에서 별점 평균 집계 (한 번만)
  useEffect(() => {
    const fetchAverageRatings = async () => {
      try {
        const reviewsQuery = query(collection(db, 'reviews'), where('rating', '>', 0));
        const snap = await getDocs(reviewsQuery);
        const acc: Record<string, { sum: number; count: number }> = {};
        snap.forEach((reviewDoc) => {
          const data = reviewDoc.data();
          const rating = Number(data.rating);
          const gameId = data.gameId;
          if (!gameId || !rating || rating <= 0) return;
          if (!acc[gameId]) acc[gameId] = { sum: 0, count: 0 };
          acc[gameId].sum += rating;
          acc[gameId].count += 1;
        });
        const avg: Record<string, number> = {};
        Object.entries(acc).forEach(([id, v]) => {
          avg[id] = v.sum / v.count;
        });
        setRatingsMap(avg);
      } catch (e) {
        console.error('리뷰 평균 집계 중 오류:', e);
      }
    };
    fetchAverageRatings();
  }, []);

  const getRating = (game: Game) => ratingsMap[game.id] ?? game.rating ?? 0;

  // 필터·보기 변경 시 페이지 리셋
  useEffect(() => {
    setPage(1);
  }, [search, selectedSchool, selectedGrade, selectedClass, displayMode]);

  // 고유한 학교, 학년, 반 목록 추출
  const uniqueSchools = [...new Set(games.map(game => game.schoolName).filter(Boolean))].sort();
  const uniqueGrades = [...new Set(games.map(game => game.grade).filter(Boolean))].sort((a, b) => (a || 0) - (b || 0));
  const uniqueClasses = [...new Set(games.map(game => game.class).filter(Boolean))].sort((a, b) => (a || 0) - (b || 0));

  const filteredGames = games.filter(game => {
    const matchesSearch = game.title.toLowerCase().includes(search.toLowerCase()) ||
                         game.schoolName.toLowerCase().includes(search.toLowerCase()) ||
                         game.creatorName.toLowerCase().includes(search.toLowerCase());
    
    const matchesSchool = !selectedSchool || game.schoolName === selectedSchool;
    const matchesGrade = !selectedGrade || game.grade === parseInt(selectedGrade);
    const matchesClass = !selectedClass || game.class === parseInt(selectedClass);
    
    return matchesSearch && matchesSchool && matchesGrade && matchesClass;
  });

  // rating 정렬은 reviews 기반 평균으로 클라이언트에서 재정렬
  const sortedGames = useMemo(() => {
    if (sortBy !== 'rating') return filteredGames;
    return [...filteredGames].sort((a, b) => (ratingsMap[b.id] ?? b.rating ?? 0) - (ratingsMap[a.id] ?? a.rating ?? 0));
  }, [filteredGames, sortBy, ratingsMap]);

  // 페이지네이션 계산
  const indexOfLastGame = page * gamesPerPage;
  const indexOfFirstGame = indexOfLastGame - gamesPerPage;
  const currentGames = sortedGames.slice(indexOfFirstGame, indexOfLastGame);
  const totalPages = Math.ceil(sortedGames.length / gamesPerPage);

  const handlePageChange = (value: number) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleResetFilters = () => {
    setSearch('');
    setSelectedSchool('');
    setSelectedGrade('');
    setSelectedClass('');
    setSortBy('createdAt');
    setDisplayMode('card5');
    setPage(1);
  };

  const renderStars = (rating: number) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {[...Array(5)].map((_, i) => (
        <span key={i} style={{ fontSize: '16px', color: i < Math.round(rating) ? '#FFD700' : '#E0E0E0' }}>
          ★
        </span>
      ))}
      <span style={{ fontSize: '14px', color: '#A7A7BC', marginLeft: '4px', fontFamily: 'Pretendard' }}>
        {rating.toFixed(1)}
      </span>
    </div>
  );

  const handleGameClick = (gameId: string) => {
    navigate(`/makecodearcade/game/${gameId}`);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px',
        width: '100%',
        height: '100%'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3', 
          borderTop: '4px solid #FF6F0F', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={getMakecodeArcadePageRootStyle(isMobile, {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      })}
    >
        <div style={{ marginBottom: '24px', flexShrink: 0 }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: 600, 
            marginBottom: '16px',
            color: '#F4F4F8',
            fontFamily: 'Pretendard'
          }}>
            게임 스토어
          </h1>
          
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'flex-end', flexShrink: 0 }}>
            <div style={{ flexGrow: 1, minWidth: '200px', display: 'flex', alignItems: 'flex-end' }}>
              <InputField
                value={search}
                onChange={setSearch}
                placeholder="검색"
                iconImage="/images/icons/Search.svg"
                height="48px"
              />
            </div>
            
            <div style={{ minWidth: '150px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: 500,
                color: '#F4F4F8',
                fontFamily: 'Pretendard'
              }}>
                학교
              </label>
              <select
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                style={selectStyle}
              >
                <option value="">전체</option>
                {uniqueSchools.map((school) => (
                  <option key={school} value={school}>
                    {school}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: '120px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: 500,
                color: '#F4F4F8',
                fontFamily: 'Pretendard'
              }}>
                학년
              </label>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                style={selectStyle}
              >
                <option value="">전체</option>
                {uniqueGrades.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}학년
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: '120px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: 500,
                color: '#F4F4F8',
                fontFamily: 'Pretendard'
              }}>
                반
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                style={selectStyle}
              >
                <option value="">전체</option>
                {uniqueClasses.map((classNum) => (
                  <option key={classNum} value={classNum}>
                    {classNum}반
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ minWidth: '150px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: 500,
                color: '#F4F4F8',
                fontFamily: 'Pretendard'
              }}>
                정렬 기준
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={selectStyle}
              >
                <option value="createdAt">최신순</option>
                <option value="playCount">플레이 수</option>
                <option value="rating">평점</option>
                <option value="title">제목</option>
              </select>
            </div>

            <div style={{ minWidth: '220px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#F4F4F8',
                fontFamily: 'Pretendard',
              }}>
                보기
              </label>
              <div style={{ display: 'flex', gap: '8px', height: '48px', alignItems: 'stretch' }}>
                {([
                  { id: 'card5' as DisplayMode, label: '5개' },
                  { id: 'card10' as DisplayMode, label: '10개' },
                  { id: 'list' as DisplayMode, label: '목록' },
                ]).map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setDisplayMode(mode.id)}
                    style={{
                      flex: 1,
                      padding: '0 12px',
                      border: displayMode === mode.id ? '1.2px solid #FF6F0F' : '1.2px solid #2E2E3C',
                      borderRadius: '8px',
                      backgroundColor: displayMode === mode.id ? '#3A2418' : '#1C1C28',
                      color: displayMode === mode.id ? '#FF6F0F' : '#F4F4F8',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: displayMode === mode.id ? 600 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                variant="outline"
                onClick={handleResetFilters}
                width="100px"
              >
                필터 초기화
              </Button>
            </div>
          </div>
        </div>

        {displayMode === 'list' ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}>
            {currentGames.map((game) => (
              <div
                key={game.id}
                onClick={() => handleGameClick(game.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: '#1C1C28',
                  borderRadius: '12px',
                  border: '1px solid #2E2E3C',
                  cursor: 'pointer',
                  flexWrap: 'wrap',
                }}
              >
                <img
                  src={game.iconUrl || DEFAULT_GAME_ICON}
                  alt={game.title}
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '8px',
                    objectFit: 'cover',
                    background: '#F8F8F8',
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = DEFAULT_GAME_ICON;
                  }}
                />
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#F4F4F8', marginBottom: '6px', fontFamily: 'Pretendard' }}>
                    {game.title}
                  </div>
                  <div style={{ fontSize: '14px', color: '#A7A7BC', marginBottom: '4px', fontFamily: 'Pretendard' }}>
                    {game.schoolName} · {game.creatorName}
                    {game.grade && game.class ? ` · ${game.grade}학년 ${game.class}반` : ''}
                  </div>
                  <div style={{ fontSize: '14px', color: '#A7A7BC', fontFamily: 'Pretendard' }}>
                    플레이 {game.playCount.toLocaleString()}회
                    {game.createdAt ? ` · 등록 ${game.createdAt.toLocaleDateString()}` : ''}
                  </div>
                </div>
                {renderStars(getRating(game))}
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: windowWidth < 768
              ? 'repeat(1, 1fr)'
              : displayMode === 'card10'
                ? 'repeat(5, minmax(180px, 243px))'
                : 'repeat(5, 243px)',
            gridAutoRows: '1fr',
            gap: '16px',
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            justifyContent: 'flex-start',
          }}>
            {currentGames.map((game) => (
              <Card
                key={game.id}
                width={windowWidth < 768 ? '100%' : '243px'}
                padding="16px"
                background="#1C1C28"
                boxShadow="none"
                onClick={() => handleGameClick(game.id)}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  boxSizing: 'border-box',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 0 5px 0 rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  position: 'relative',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  borderRadius: '12px',
                  marginBottom: '16px',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}>
                  <ArcadeGamePoster id={game.id} title={game.title} iconUrl={game.iconUrl} />
                </div>

                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  minWidth: 0,
                  width: '100%',
                  overflow: 'hidden',
                  gap: '6px',
                  marginTop: 'auto',
                }}>
                  <div>
                    <h3 style={{
                      margin: '0 0 6px 0',
                      fontSize: '18px',
                      fontWeight: 600,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      color: '#F4F4F8',
                      fontFamily: 'Pretendard',
                    }}>
                      {game.title}
                    </h3>
                    <p style={{
                      margin: '0 0 6px 0',
                      fontSize: '14px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#A7A7BC',
                      fontFamily: 'Pretendard',
                    }}>
                      제작자: {game.creatorName}
                    </p>
                    <p style={{
                      margin: '0 0 6px 0',
                      fontSize: '14px',
                      color: '#A7A7BC',
                      fontFamily: 'Pretendard',
                    }}>
                      플레이 {game.playCount.toLocaleString()}회
                    </p>
                  </div>
                  {renderStars(getRating(game))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {currentGames.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '48px 16px',
            color: '#989898',
            fontSize: '14px',
            fontFamily: 'Pretendard',
          }}>
            조건에 맞는 게임이 없습니다.
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px', marginBottom: '0', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => handlePageChange(1)}
                disabled={page === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #F0F0F0',
                  borderRadius: '8px',
                  backgroundColor: page === 1 ? '#2A2A36' : '#1C1C28',
                  color: page === 1 ? '#989898' : '#161616',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Pretendard'
                }}
              >
                처음
              </button>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #F0F0F0',
                  borderRadius: '8px',
                  backgroundColor: page === 1 ? '#2A2A36' : '#1C1C28',
                  color: page === 1 ? '#989898' : '#161616',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Pretendard'
                }}
              >
                이전
              </button>
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #F0F0F0',
                        borderRadius: '8px',
                        backgroundColor: page === pageNum ? '#FF6F0F' : '#1C1C28',
                        color: page === pageNum ? 'white' : '#F4F4F8',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: page === pageNum ? 600 : 400
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} style={{ padding: '8px', color: '#989898' }}>...</span>;
                }
                return null;
              })}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #F0F0F0',
                  borderRadius: '8px',
                  backgroundColor: page === totalPages ? '#2A2A36' : '#1C1C28',
                  color: page === totalPages ? '#989898' : '#161616',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Pretendard'
                }}
              >
                다음
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={page === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #F0F0F0',
                  borderRadius: '8px',
                  backgroundColor: page === totalPages ? '#2A2A36' : '#1C1C28',
                  color: page === totalPages ? '#989898' : '#161616',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Pretendard'
                }}
              >
                마지막
              </button>
            </div>
          </div>
        )}

        {/* 검색 결과 요약 */}
        <div style={{ textAlign: 'center', marginTop: '16px', marginBottom: '32px' }}>
          <p style={{ 
            fontSize: '14px', 
            color: '#989898',
            fontFamily: 'Pretendard',
            margin: 0
          }}>
            총 {filteredGames.length}개의 게임 중 {filteredGames.length === 0 ? 0 : indexOfFirstGame + 1}-{Math.min(indexOfLastGame, filteredGames.length)}개 표시 중
          </p>
        </div>
    </div>
  );
};

export default Store; 